// The fonts a theme names load from Google Fonts: any family there, not only those below. A theme sets its fonts once, in
// font-ui, font-prose, font-mono and font-display; the app loads the faces they name first. A system face loads nothing,
// and Lureo's own faces (BUNDLED) come with the app, so Lureo Light and Lureo Dark never ask Google for anything.

/** The faces whose exact Google Fonts css2 axes are known, loaded with just those (a weight a face lacks fails a css2 request). */
const FACES: Record<string, string> = {
  'Space Grotesk': 'wght@300;400;500;600;700',
  Manrope: 'wght@300;400;500;600;700',
  'IBM Plex Sans': 'wght@300;400;500;600;700',
  'Work Sans': 'wght@300;400;500;600;700',
  Outfit: 'wght@300;400;500;600;700',
  Sora: 'wght@300;400;500;600;700',
  Figtree: 'wght@300;400;500;600;700',
  Lexend: 'wght@300;400;500;600;700',
  'Nunito Sans': 'wght@300;400;500;600;700',
  'Plus Jakarta Sans': 'wght@300;400;500;600;700',
  'Fira Sans': 'wght@300;400;500;600;700',
  'Libre Baskerville': 'wght@400;700',
  'Source Serif 4': 'opsz,wght@8..60,300;8..60,400;8..60,500;8..60,600;8..60,700',
  Newsreader: 'opsz,wght@6..72,300;6..72,400;6..72,500;6..72,600;6..72,700',
  Lora: 'wght@400;500;600;700',
  Literata: 'opsz,wght@7..72,300;7..72,400;7..72,500;7..72,600;7..72,700',
  'IBM Plex Serif': 'wght@300;400;500;600;700',
  'Fira Code': 'wght@400;500;600;700',
  'IBM Plex Mono': 'wght@400;500',
  'Space Mono': 'wght@400;700',
  'Source Code Pro': 'wght@400;500',
};

/**
 * The faces with a width axis that sets give a title width, with Google's width and weight ranges: a title width other than
 * 100% loads the display face across its widths. Google refuses a width axis a face lacks, so a face not listed loads as usual
 * and its titles keep their normal width.
 */
const WIDTH_FACES: Record<string, { wdth: [number, number]; wght: [number, number] }> = {
  Archivo: { wdth: [62, 125], wght: [100, 900] },
  'Bricolage Grotesque': { wdth: [75, 100], wght: [200, 800] },
  Fredoka: { wdth: [75, 125], wght: [300, 700] },
  'TikTok Sans': { wdth: [75, 150], wght: [300, 900] },
};

/** The text faces the theme generator picks from. */
export const UI_FACES = ['Space Grotesk', 'Manrope', 'IBM Plex Sans', 'Work Sans', 'Outfit', 'Sora', 'Figtree', 'Lexend', 'Nunito Sans', 'Plus Jakarta Sans'];

/** The monospace faces the theme generator picks from. */
export const MONO_FACES = ['JetBrains Mono', 'IBM Plex Mono', 'Space Mono', 'Fira Code', 'Source Code Pro'];

/** The face a font stack names first ('Space Grotesk', ui-sans-serif, … names Space Grotesk). */
const faceOf = (stack: string | undefined) => stack?.match(/^\s*['"]?([^,'"]+)/)?.[1]?.trim();

/** A font token's stack, following a link to another (prose that is var(--lureo-font-ui) is the interface face). */
const stackOf = (tokens: Record<string, string>, name: string, depth = 0): string | undefined => {
  const linked = tokens[name]?.match(/^var\(--([\w-]+)\)$/)?.[1];
  return linked && depth < 4 ? stackOf(tokens, linked, depth + 1) : tokens[name];
};

/** The theme's font tokens, whose faces it loads. */
const FONT_TOKENS = ['lureo-font-ui', 'lureo-font-prose', 'lureo-font-mono', 'lureo-font-display', 'lureo-font-label'];

/** Lureo's own faces, served with the app from public/fonts by the @font-face rules in index.css. */
const BUNDLED = new Set(['Inter', 'Fraunces', 'JetBrains Mono']);

/** Faces every platform has, and CSS's names for the system's own: nothing to load. */
const SYSTEM_FACES = new Set([
  'ui-sans-serif', 'ui-serif', 'ui-monospace', 'ui-rounded', 'system-ui', 'sans-serif', 'serif', 'monospace', 'cursive', 'fantasy',
  '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Helvetica', 'Helvetica Neue', 'Arial', 'Georgia', 'Times', 'Times New Roman',
  'Menlo', 'Monaco', 'Consolas', 'Courier', 'Courier New', 'SFMono-Regular', 'Liberation Mono',
]);

/**
 * The stylesheet for one face. A face listed above asks for exactly its axes; any other uses Google's older css API, which
 * serves whichever of the weights 100 to 900 the family has, where css2 refuses a weight it lacks.
 */
function stylesheetOf(face: string, wide: boolean) {
  const family = face.replace(/ /g, '+');
  const axes = wide ? WIDTH_FACES[face] : undefined;
  if (axes) return `https://fonts.googleapis.com/css2?family=${family}:wdth,wght@${axes.wdth[0]}..${axes.wdth[1]},${axes.wght[0]}..${axes.wght[1]}&display=swap`;
  return face in FACES
    ? `https://fonts.googleapis.com/css2?family=${family}:${FACES[face]}&display=swap`
    : `https://fonts.googleapis.com/css?family=${family}:100,200,300,400,500,600,700,800,900&display=swap`;
}

/**
 * The stylesheets that load the faces a theme's font-ui, font-prose, font-mono, font-display and font-label name: one each, so a name Google
 * doesn't have fails alone, and none for a system or bundled face. The display face loads across its widths when titles are set
 * wider or narrower.
 */
export function fontStylesheets(tokens: Record<string, string>): string[] {
  const faces = [...new Set(FONT_TOKENS.map(name => faceOf(stackOf(tokens, name))))].filter((face): face is string => !!face && !SYSTEM_FACES.has(face) && !BUNDLED.has(face));
  const display = faceOf(stackOf(tokens, 'lureo-font-display'));
  const wide = (tokens['lureo-title-width'] ?? '100%').trim() !== '100%';
  return faces.map(face => stylesheetOf(face, wide && face === display));
}
